import { ReactNode, useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import Tooltip from '../tooltip/Tooltip';

type ColorOption = 'gray' | 'red' | 'yellow' | 'green' | 'blue' | 'purple';

function randomColor(): ColorOption {
    const colors = ['gray', 'red', 'yellow', 'green', 'blue', 'purple'];
    return colors[Math.floor(Math.random() * colors.length)] as ColorOption;
}

const colors = {
    gray: '#6b7280',
    red: '#ef4444',
    yellow: '#eab308',
    blue: '#3b82f6',
    green: '#22c55e',
    purple: '#a855f7',
};

const sizes: { [key: string]: string } = {
    xs: 'h-6 w-6',
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
    xl: 'h-14 w-14',
};

const texts: { [key: string]: string } = {
    xs: 'text-xs',
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
};

const types: { [key: string]: string } = {
    circle: 'rounded-full',
    rounded: 'rounded-md',
};

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export type AvatarProps = {
    size?: Size;
    alt: string;
    color?: ColorOption | string;
    src?: string | null;
    ring?: boolean;
    type?: 'circle' | 'rounded';
    tooltip?: string;
    tooltipDirection?: 'top' | 'right' | 'bottom' | 'left';
    badge?: ReactNode;
    onClick?: () => void;
    onRemove?: () => void;
};

function Avatar({
    size = 'md',
    src,
    alt,
    tooltip,
    tooltipDirection = 'top',
    color = 'gray',
    type = 'circle',
    ring = false,
    badge,
    onClick,
    onRemove,
}: AvatarProps): JSX.Element {
    const [hasSrc, setHasSrc] = useState(Boolean(src));
    const ringStyle = ring ? 'ring-2 dark:ring-[#333333] ring-white' : '';
    const _col = color in colors ? colors[color as ColorOption] : color;
    const [show, setShow] = useState(false);

    useEffect(() => {
        setHasSrc(Boolean(src));
    }, [src]);

    function handleEnter(): void {
        setShow(true);
    }

    function handleLeave(): void {
        setShow(false);
    }

    const baseAvatar = hasSrc ? (
        <img className={`inline-block ${sizes[size]} ${types[type]} ${ringStyle}`} src={src} onError={(): void => setHasSrc(false)} />
    ) : (
        <span
            className={`select-none ${ringStyle} inline-flex items-center justify-center ${sizes[size]} ${types[type]}`}
            style={{ backgroundColor: _col }}
        >
            <span className={`${texts[size]} leading-none text-white uppercase`}>{alt}</span>
        </span>
    );

    return (
        <Tooltip message={tooltip} position={tooltipDirection} disabled={!tooltip}>
            <div className="relative w-fit" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
                {onRemove && show && (
                    <div className="absolute -top-1 -right-1 cursor-pointer bg-gray-600 rounded-full p-0.5" onClick={onRemove}>
                        <XMarkIcon className={`${size === 'xs' ? 'w-2.5 h-2.5' : 'w-3 h-3'} text-white`} />
                    </div>
                )}
                <div className={`${onClick ? 'cursor-pointer' : ''}`} onClick={onClick}>
                    {baseAvatar}
                </div>
                {badge && <div className="absolute -bottom-0.5 right-0">{badge}</div>}
            </div>
        </Tooltip>
    );
}

export default Avatar;
